import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { keyframes } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CssBaseline from '@mui/material/CssBaseline';
import SpeedIcon from '@mui/icons-material/Speed';
import TimelineIcon from '@mui/icons-material/Timeline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TuneIcon from '@mui/icons-material/Tune';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import AppTheme from '../shared-theme/AppTheme';
import { useAuth } from '../context/AuthContext';
import LandingSensorOverview from '../components/LandingSensorOverview';

// Import all meters - add new meters here and they'll automatically appear in the showcase
import {
  GaugeMeter,
  ThermometerMeter,
  LevelMeter,
  HumidityMeter,
  CO2Meter,
  PARMeter,
  TimeSeriesPlot,
} from '../dashboard/components/meters';

// =============================================================================
// HERO CONFIGURATION - Edit these values to customize the hero section
// =============================================================================

const HERO_CONFIG = {
  // -------------------------------------------------------------------------
  // LOGO SETTINGS
  // -------------------------------------------------------------------------
  // Option 1: Use text title (set useLogo to false)
  // Option 2: Use logo image (set useLogo to true and provide logoPath)
  useLogo: true,

  // Path to your logo (relative to public folder or absolute URL)
  // Example: '/logo.svg' or '/images/my-logo.svg'
  logoPath: '/logo.svg',
  logoWidth: 1000,
  logoHeight: 200,

  // Text title (used when useLogo is false)
  title: 'myGrow Collective',

  // Tagline/subtitle
  tagline: 'An app for monitoring, analyzing, and sharing your grows and experience.',

  // -------------------------------------------------------------------------
  // BACKGROUND SETTINGS
  // -------------------------------------------------------------------------
  // Option 1: Use gradient background (set useBackgroundMedia to false)
  // Option 2: Use background image/GIF/video (set useBackgroundMedia to true)
  useBackgroundMedia: true,

  // Background type: 'image' or 'video'
  // Use 'video' for .webm, .mp4 files (better performance than GIF)
  // Use 'image' for .png, .jpg, .gif, .webp files
  backgroundType: 'video',  // 'image' or 'video'

  // DESKTOP background - supports:
  // Images: PNG, JPG, GIF, WebP
  // Videos: WebM, MP4 (WebM recommended for smaller file size)
  // Files should be placed in the /public folder
  //
  // Examples:
  // backgroundMediaPath: '/hero-background.png',   // Static PNG image
  // backgroundMediaPath: '/hero-background.gif',   // Animated GIF
  // backgroundMediaPath: '/hero-background.webm',  // WebM video (recommended)
  // backgroundMediaPath: '/hero-background.mp4',   // MP4 video
  //
  backgroundMediaPath: '/landing_bg.webm',  // <-- DESKTOP BACKGROUND

  // MOBILE background - use a different media file optimized for mobile
  // Set to null to use the same as desktop
  //
  // Examples:
  // mobileBackgroundPath: null,                       // Use same as desktop
  // mobileBackgroundPath: '/hero-mobile.webm',        // Mobile-specific WebM video
  // mobileBackgroundPath: '/hero-mobile.gif',         // Mobile-specific GIF
  //
  mobileBackgroundPath: '/landing_bg_mobile.webm',  // <-- MOBILE BACKGROUND (null = use desktop)

  // Gradient background (used when useBackgroundImage is false)
  gradientBackground: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',

  // Background overlay - adds dark layer for text readability
  // Increase opacity (e.g., 0.6, 0.7) for brighter/busier backgrounds
  // Decrease opacity (e.g., 0.3, 0.4) for darker backgrounds
  backgroundOverlay: 'rgba(0, 0, 0, 0.5)',

  // Background position - controls which part of the image is visible
  // 'top center' = show top of image, crop bottom (wider FOV)
  // 'center' = center the image, crop top and bottom equally
  // 'bottom center' = show bottom of image, crop top
  backgroundPosition: 'top center',
};

// =============================================================================

// Sensor showcase data - each entry creates a sensor module card
// Add new sensors here and they'll automatically be included in the scrolling showcase
const SENSOR_SHOWCASE = [
  {
    id: 'ph',
    label: 'pH Level',
    value: 6.2,
    unit: '',
    MeterComponent: GaugeMeter,
    meterProps: { min: 0, max: 14, optimalMin: 5.8, optimalMax: 6.5, segments: 7, colors: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#eab308', '#f97316', '#ef4444'] },
  },
  {
    id: 'temp',
    label: 'Temperature',
    value: 75,
    unit: '°F',
    MeterComponent: ThermometerMeter,
    meterProps: { min: 50, max: 100, optimalMin: 70, optimalMax: 80 },
  },
  {
    id: 'humidity',
    label: 'Humidity',
    value: 62,
    unit: '%',
    MeterComponent: HumidityMeter,
    meterProps: { min: 0, max: 100, optimalMin: 55, optimalMax: 70 },
  },
  {
    id: 'temp-graph',
    label: 'Temp History',
    value: 74,
    unit: '°F',
    MeterComponent: TimeSeriesPlot,
    meterProps: { min: 50, max: 100, optimalMin: 70, optimalMax: 80, color: '#ef4444' },
  },
  {
    id: 'co2',
    label: 'CO2',
    value: 1100,
    unit: 'ppm',
    MeterComponent: CO2Meter,
    meterProps: { min: 300, max: 2000, optimalMin: 800, optimalMax: 1200 },
  },
  {
    id: 'ec',
    label: 'EC Level',
    value: 1.8,
    unit: 'mS/cm',
    MeterComponent: GaugeMeter,
    meterProps: { min: 0, max: 3.5, optimalMin: 1.2, optimalMax: 2.2, segments: 5, colors: ['#06b6d4', '#0891b2', '#22c55e', '#0891b2', '#06b6d4'] },
  },
  {
    id: 'par',
    label: 'PAR (PPFD)',
    value: 850,
    unit: 'µmol/m²/s',
    MeterComponent: PARMeter,
    meterProps: { min: 0, max: 1500, optimalMin: 600, optimalMax: 1000, rgb: { r: 185, g: 120, b: 45 } },
  },
  {
    id: 'humidity-graph',
    label: 'Humidity History',
    value: 58,
    unit: '%',
    MeterComponent: TimeSeriesPlot,
    meterProps: { min: 0, max: 100, optimalMin: 55, optimalMax: 70, color: '#3b82f6' },
  },
  {
    id: 'water-level',
    label: 'Water Level',
    value: 78,
    unit: '%',
    MeterComponent: LevelMeter,
    meterProps: { min: 0, max: 100, optimalMin: 60, optimalMax: 90, color: '#0ea5e9' },
  },
  {
    id: 'water-temp',
    label: 'Water Temp',
    value: 68,
    unit: '°F',
    MeterComponent: ThermometerMeter,
    meterProps: { min: 50, max: 90, optimalMin: 65, optimalMax: 72, color: '#14b8a6' },
  },
  {
    id: 'ph-graph',
    label: 'pH History',
    value: 6.1,
    unit: '',
    MeterComponent: TimeSeriesPlot,
    meterProps: { min: 0, max: 14, optimalMin: 5.8, optimalMax: 6.5, color: '#a855f7' },
  },
  {
    id: 'vpd',
    label: 'VPD',
    value: 1.2,
    unit: 'kPa',
    MeterComponent: GaugeMeter,
    meterProps: { min: 0, max: 2.5, optimalMin: 0.8, optimalMax: 1.4, segments: 5, colors: ['#f97316', '#fbbf24', '#22c55e', '#fbbf24', '#f97316'] },
  },
];

// Features data - Compact descriptions
const features = [
  {
    icon: <SpeedIcon />,
    title: 'Real-Time Monitoring',
    description: 'Live sensor data for pH, EC, temperature, humidity and more.',
  },
  {
    icon: <TimelineIcon />,
    title: 'Grow Tracking',
    description: 'Track grow cycles from seedling to harvest.',
  },
  {
    icon: <NotificationsActiveIcon />,
    title: 'Smart Reminders',
    description: 'Customizable reminders for feeding and maintenance.',
  },
  {
    icon: <TuneIcon />,
    title: 'Preset Profiles',
    description: 'Optimal ranges for different grow stages.',
  },
  {
    icon: <CloudIcon />,
    title: 'Cloud Sync',
    description: 'Access your data from anywhere.',
  },
  {
    icon: <SecurityIcon />,
    title: 'Secure & Private',
    description: 'Encrypted and private data.',
  },
];

// Infinite scroll animation - scrolls through 1/3 of content (one full set) then loops seamlessly
const scrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-33.333%);
  }
`;

// Sensor Card Component for showcase - Fixed size for consistency
function SensorShowcaseCard({ sensor }) {
  const { MeterComponent, meterProps, label, value, unit } = sensor;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.5,
        width: 140,
        minWidth: 140,
        height: 180,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(30, 30, 30, 0.85)',
        backdropFilter: 'blur(8px)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        flexShrink: 0,
      }}
    >
      <Stack spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
        {/* Title */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontFamily: 'Inter, sans-serif',
            textAlign: 'center',
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>

        {/* Meter visualization - fixed size */}
        <Box
          sx={{
            width: '100%',
            height: 85,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <MeterComponent value={value} size={75} {...meterProps} />
        </Box>

        {/* Value display - always at bottom */}
        <Box sx={{ mt: 'auto', width: '100%' }}>
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.8rem',
                color: 'success.main',
              }}
            >
              {value}{unit && ` ${unit}`}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Card>
  );
}

// Scrolling Sensor Showcase Component - Infinite loop scroll
function SensorShowcase() {
  // Triple the sensors for truly seamless infinite scroll
  const triplicatedSensors = [...SENSOR_SHOWCASE, ...SENSOR_SHOWCASE, ...SENSOR_SHOWCASE];

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        // Extend below for mobile browser chrome with blurred background
        py: 1,
        pb: { xs: 'calc(8px + env(safe-area-inset-bottom, 20px))', md: 1 },
        bgcolor: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          // Scroll through 1/3 of content (one full set) then seamlessly loop
          animation: `${scrollAnimation} 30s linear infinite`,
          // Use will-change for smoother animation
          willChange: 'transform',
          '&:hover': {
            animationPlayState: 'paused',
          },
        }}
      >
        {triplicatedSensors.map((sensor, index) => (
          <Box key={`${sensor.id}-${index}`} sx={{ px: 1, flexShrink: 0 }}>
            <SensorShowcaseCard sensor={sensor} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function LandingPage(props) {
  const { isAuthenticated } = useAuth();

  // Determine which background to use for mobile vs desktop
  const mobileBackground = HERO_CONFIG.mobileBackgroundPath || HERO_CONFIG.backgroundMediaPath;
  const desktopBackground = HERO_CONFIG.backgroundMediaPath;
  const isVideo = HERO_CONFIG.backgroundType === 'video';

  // Build hero background style - only used for gradient or image backgrounds
  const heroBackgroundStyle = HERO_CONFIG.useBackgroundMedia && !isVideo
    ? {
        backgroundSize: 'cover',
        backgroundPosition: HERO_CONFIG.backgroundPosition,
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        // Responsive background image - mobile first, then desktop
        backgroundImage: {
          xs: `url(${mobileBackground})`,
          md: `url(${desktopBackground})`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: HERO_CONFIG.backgroundOverlay,
        },
      }
    : HERO_CONFIG.useBackgroundMedia && isVideo
    ? {
        position: 'relative',
      }
    : {
        background: HERO_CONFIG.gradientBackground,
      };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          // Use dynamic viewport height (dvh) for mobile browser compatibility
          // Falls back to vh for browsers that don't support dvh
          minHeight: '100dvh',
          height: '100dvh',
          bgcolor: 'background.default',
          overflowY: 'auto',
          scrollSnapType: 'y mandatory',
          // Fallback for older browsers
          '@supports not (height: 100dvh)': {
            minHeight: '100vh',
            height: '100vh',
          },
        }}
      >
        {/* SCREEN 1: Hero + Sensor Showcase - Full viewport height */}
        <Box
          sx={{
            // Use dvh for proper mobile viewport sizing
            height: '100dvh',
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
            // Safe area padding for notches and browser UI
            pt: 'env(safe-area-inset-top, 0px)',
            pb: 'env(safe-area-inset-bottom, 0px)',
            pl: 'env(safe-area-inset-left, 0px)',
            pr: 'env(safe-area-inset-right, 0px)',
            // Fallback for older browsers
            '@supports not (height: 100dvh)': {
              height: '100vh',
              minHeight: '100vh',
            },
            ...heroBackgroundStyle,
          }}
        >
          {/* Video Background - only rendered if using video */}
          {HERO_CONFIG.useBackgroundMedia && isVideo && (
            <>
              {/* Mobile video */}
              <Box
                component="video"
                autoPlay
                loop
                muted
                playsInline
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: HERO_CONFIG.backgroundPosition,
                  zIndex: 0,
                  display: { xs: 'block', md: 'none' },
                }}
              >
                <source src={mobileBackground} type={`video/${mobileBackground.split('.').pop()}`} />
              </Box>
              {/* Desktop video */}
              <Box
                component="video"
                autoPlay
                loop
                muted
                playsInline
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: HERO_CONFIG.backgroundPosition,
                  zIndex: 0,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <source src={desktopBackground} type={`video/${desktopBackground.split('.').pop()}`} />
              </Box>
              {/* Overlay for video */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: HERO_CONFIG.backgroundOverlay,
                  zIndex: 0,
                }}
              />
            </>
          )}

          {/* Hero content - takes available space, centered */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Container maxWidth="lg">
              <Stack spacing={2} alignItems="center" textAlign="center">
              {/* Icon + Logo or Title */}
              {HERO_CONFIG.useLogo ? (
                <>
                  <Box
                    component="img"
                    src="/icon.svg"
                    alt=""
                    loading="eager"
                    decoding="async"
                    sx={{
                      width: { xs: 80, md: 100 },
                      height: { xs: 80, md: 100 },
                      objectFit: 'contain',
                    }}
                  />
                  <Box
                    component="img"
                    src={HERO_CONFIG.logoPath}
                    alt={HERO_CONFIG.title}
                    loading="eager"
                    decoding="async"
                    sx={{
                      width: { xs: 300, md: 450 },
                      height: { xs: 85, md: 130 },
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </>
              ) : (
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: '2rem', md: '2.75rem' },
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {HERO_CONFIG.title}
                </Typography>
              )}

              {/* Tagline - Shorter */}
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  color: HERO_CONFIG.useBackgroundImage ? 'common.white' : 'text.secondary',
                  maxWidth: 500,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                Professional grow room monitoring and management
              </Typography>

              {/* CTA Buttons - blur background with white border */}
              <Stack direction="row" spacing={2} justifyContent="center">
                {isAuthenticated ? (
                  <Box
                    component={RouterLink}
                    to="/app"
                    sx={{
                      px: 4,
                      py: 1.25,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      textDecoration: 'none',
                      fontSize: '1rem',
                      minWidth: 140,
                      textAlign: 'center',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.25)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      display: 'inline-block',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderColor: 'rgba(255, 255, 255, 0.6)',
                      },
                    }}
                  >
                    Dashboard
                  </Box>
                ) : (
                  <Box
                    component={RouterLink}
                    to="/login"
                    sx={{
                      px: 4,
                      py: 1.25,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      textDecoration: 'none',
                      fontSize: '1rem',
                      minWidth: 140,
                      textAlign: 'center',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.25)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      display: 'inline-block',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderColor: 'rgba(255, 255, 255, 0.6)',
                      },
                    }}
                  >
                    Log In
                  </Box>
                )}
                <Box
                  component={RouterLink}
                  to="/demo"
                  sx={{
                    px: 4,
                    py: 1.25,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    textDecoration: 'none',
                    fontSize: '1rem',
                    minWidth: 140,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.25)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    display: 'inline-block',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      borderColor: 'rgba(255, 255, 255, 0.6)',
                    },
                  }}
                >
                  Try Demo
                </Box>
              </Stack>
            </Stack>
            </Container>
          </Box>

          {/* Sensor Showcase - At bottom of first screen */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <SensorShowcase />
          </Box>

        </Box>

        {/* SCREEN 2: Features + Sensor Overview - Full viewport height */}
        <Box
          sx={{
            // Use dvh for proper mobile viewport sizing
            minHeight: '100dvh',
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            py: { xs: 1, md: 2 },
            position: 'relative',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
            // Safe area padding for notches and browser UI - less top padding on desktop
            pt: { xs: `calc(8px + env(safe-area-inset-top, 0px))`, md: 3 },
            pb: { xs: `calc(8px + env(safe-area-inset-bottom, 0px))`, md: 3 },
            pl: 'env(safe-area-inset-left, 0px)',
            pr: 'env(safe-area-inset-right, 0px)',
            // Fallback for older browsers
            '@supports not (height: 100dvh)': {
              minHeight: '100vh',
              height: '100vh',
            },
          }}
        >
          {/* Features Section - Compact on mobile */}
          <Container maxWidth="lg" sx={{ mb: { xs: 1, md: 1 }, flexShrink: 0 }}>
            <Stack spacing={0.5} alignItems="center" sx={{ mb: { xs: 1, md: 1 } }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  textAlign: 'center',
                  fontSize: { xs: '1rem', md: '1.5rem' },
                }}
              >
                Everything you need to grow better
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  color: 'text.secondary',
                  textAlign: 'center',
                  maxWidth: 500,
                  fontSize: { xs: '0.7rem', md: '0.875rem' },
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Real-time monitoring and smart automation for optimal cultivation
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                gap: { xs: 0.75, md: 1.5 },
                maxWidth: 900,
                mx: 'auto',
              }}
            >
              {features.map((feature, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{
                    p: { xs: 1.25, md: 1.5 },
                    minHeight: { xs: 85, md: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: 'primary.main',
                      mb: { xs: 0.25, md: 0.5 },
                      '& .MuiSvgIcon-root': { fontSize: { xs: 16, md: 24 } },
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '0.65rem', md: '0.8rem' },
                      mb: 0.25,
                      lineHeight: 1.2,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: 1.2,
                      fontSize: { xs: '0.5rem', md: '0.7rem' },
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Container>

          {/* Live Sensor Overview Section - fills remaining height */}
          <Container
            maxWidth="lg"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              px: { xs: 1, md: 3 },
              pb: { xs: 1, md: 2 },
            }}
          >
            <Stack spacing={0.5} alignItems="center" sx={{ mb: { xs: 0.5, md: 1 }, flexShrink: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  textAlign: 'center',
                  fontSize: { xs: '0.85rem', md: '1.25rem' },
                }}
              >
                Real-Time Sensor Monitoring
              </Typography>
            </Stack>
            <Box sx={{ flex: 1, minHeight: { xs: 180, md: 300 } }}>
              <LandingSensorOverview />
            </Box>
          </Container>
        </Box>

        {/* SCREEN 3: CTA + Footer - Full viewport height */}
        <Box
          sx={{
            // Use dvh for proper mobile viewport sizing
            minHeight: '100dvh',
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
            // Safe area padding for notches and browser UI
            pt: 'env(safe-area-inset-top, 0px)',
            pb: 'env(safe-area-inset-bottom, 0px)',
            pl: 'env(safe-area-inset-left, 0px)',
            pr: 'env(safe-area-inset-right, 0px)',
            // Fallback for older browsers
            '@supports not (height: 100dvh)': {
              minHeight: '100vh',
              height: '100vh',
            },
          }}
        >
          {/* CTA Section - centered content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: { xs: 4, md: 6 },
            }}
          >
            <Container maxWidth="sm">
              <Stack spacing={4} alignItems="center" textAlign="center">
                {/* Icon */}
                <Box
                  component="img"
                  src="/icon.svg"
                  alt=""
                  sx={{
                    width: { xs: 60, md: 80 },
                    height: { xs: 60, md: 80 },
                    objectFit: 'contain',
                    opacity: 0.9,
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                  }}
                >
                  Ready to optimize your grow?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'Inter, sans-serif',
                    maxWidth: 450,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  Start with our demo to explore all features, or create an account to begin tracking your grows.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center">
                  {isAuthenticated ? (
                    <Box
                      component={RouterLink}
                      to="/app"
                      sx={{
                        px: 4,
                        py: 1.25,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        textDecoration: 'none',
                        fontSize: '1rem',
                        minWidth: 140,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        backgroundColor: 'action.hover',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        display: 'inline-block',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                          borderColor: 'text.secondary',
                        },
                      }}
                    >
                      Dashboard
                    </Box>
                  ) : (
                    <Box
                      component={RouterLink}
                      to="/login"
                      sx={{
                        px: 4,
                        py: 1.25,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        textDecoration: 'none',
                        fontSize: '1rem',
                        minWidth: 140,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        backgroundColor: 'action.hover',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        display: 'inline-block',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                          borderColor: 'text.secondary',
                        },
                      }}
                    >
                      Log In
                    </Box>
                  )}
                  <Box
                    component={RouterLink}
                    to="/demo"
                    sx={{
                      px: 4,
                      py: 1.25,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      textDecoration: 'none',
                      fontSize: '1rem',
                      minWidth: 140,
                      textAlign: 'center',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      color: 'text.primary',
                      backgroundColor: 'action.hover',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      display: 'inline-block',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                        borderColor: 'text.secondary',
                      },
                    }}
                  >
                    Try Demo
                  </Box>
                </Stack>
              </Stack>
            </Container>
          </Box>

          {/* Footer - at bottom */}
          <Box
            sx={{
              py: 3,
              textAlign: 'center',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              © {new Date().getFullYear()} myGrow Collective. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </AppTheme>
  );
}
